/**
 * @fileoverview Button - Reusable button component with variants
 *
 * Purpose: Provides accessible, styled button component using CVA for variants
 * Dependencies: Radix UI Slot, class-variance-authority
 * Used by: All features requiring button interactions
 *
 * Note: This file exports both component and variants (shadcn pattern)
 * Fast Refresh warning is expected and safe to ignore
 *
 * @example
 * <Button variant="default">Click me</Button>
 * <Button variant="destructive" size="lg">Delete</Button>
 * <Button variant="outline" asChild>
 *   <Link to="/home">Go Home</Link>
 * </Button>
 */

/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/misc";

/**
 * Button variants using class-variance-authority
 * Defines all button styles and size variations
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/85",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/85",
        outline:
          "border border-input bg-background hover:bg-muted hover:text-foreground",
        secondary: "bg-accent text-accent-foreground hover:bg-accent/85",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child component (using Radix Slot) */
  asChild?: boolean;
  /** Enable icon expansion animation on hover (scales to 125%) */
  expandIcon?: boolean;
}

/**
 * Button component with multiple variants and sizes
 *
 * Features:
 * - Multiple style variants (default, destructive, outline, secondary, ghost, link)
 * - Multiple size options (default, sm, lg, icon)
 * - Accessible (keyboard navigation, focus states)
 * - Can render as child component using asChild prop
 * - Optional icon expansion on hover (expandIcon prop scales SVG to 125%)
 * - Extends all native button HTML attributes
 *
 * @component
 * @example
 * // Default button
 * <Button onClick={handleClick}>Submit</Button>
 *
 * // Destructive variant
 * <Button variant="destructive">Delete</Button>
 *
 * // Button with icon expansion on hover
 * <Button expandIcon>
 *   <SaveIcon /> Save
 * </Button>
 *
 * // Icon button with expansion
 * <Button variant="ghost" size="icon" expandIcon>
 *   <TrashIcon />
 * </Button>
 *
 * // Render as Link using asChild
 * <Button asChild>
 *   <Link to="/dashboard">Dashboard</Link>
 * </Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, expandIcon = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          expandIcon && "icon-expand-hover",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
